/**
 * The sign-in controls, wherever sign-in happens: the sign-in screen, the
 * "Sign in again" sheet.
 *
 * On the web, Google renders its own button into the container (GIS). In the
 * native apps the OS sheets replace it, so these are this app's own buttons —
 * Google on both platforms, Apple beside it on iOS with **equal prominence**:
 * same size, same weight, same place in the order (App Store guideline 4.8).
 * Nothing here navigates; each button opens an in-process sheet (INV-8).
 */

import { cx } from "@/features/common/primitives";
import type { GoogleButton, SignInStatus } from "@/hooks/use_sign_in";

interface AppleSheet {
  available: boolean;
  status: SignInStatus;
  start: () => void;
}

const SHEET_BUTTON = cx(
  "flex min-h-phi-5 w-full items-center justify-center rounded-lg px-phi-0",
  "border border-line bg-bg-2 text-phi-n1 font-semibold text-tx",
  "disabled:opacity-35",
);

export function SignInButtons({
  google,
  apple,
}: {
  google: GoogleButton;
  apple: AppleSheet;
}): React.JSX.Element {
  const busy = google.status === "signing-in" || apple.status === "signing-in";
  if (!google.native) {
    // Google renders its own button here. The credential comes back to a
    // JavaScript callback — nothing navigates away (INV-8).
    return <div ref={google.containerRef} />;
  }
  return (
    <div className="flex w-full max-w-xs flex-col gap-phi-n1">
      <button type="button" className={SHEET_BUTTON} disabled={busy} onClick={google.start}>
        Continue with Google
      </button>
      {apple.available ? (
        <button type="button" className={SHEET_BUTTON} disabled={busy} onClick={apple.start}>
          Continue with Apple
        </button>
      ) : null}
    </div>
  );
}
